import { useCallback, useLayoutEffect, useRef } from 'react';
import { getSeekyToken } from '../Services/Seeky/SeekyApi';
import { chatbotOrigin, isChatbotMessage, seekyTokenUrl } from '../utils/seekyConfig';

export default function useSeekyToken({ iframeRef, enabled, isLoggedIn, userId, userRole }) {
  const requests = useRef(new Set());
  const generation = useRef(0);
  const requestNumber = useRef(0);
  const frameLoaded = useRef(false);
  const loggedOut = useRef(false);
  const previousLogin = useRef(isLoggedIn);

  const invalidateRequests = useCallback(() => {
    generation.current += 1;
    requests.current.forEach(controller => controller.abort());
    requests.current.clear();
  }, []);

  const sendSeekyToken = useCallback(async () => {
    const frameWindow = iframeRef.current?.contentWindow;
    if (!enabled || !chatbotOrigin || !seekyTokenUrl || !frameWindow) return;
    const currentGeneration = generation.current;
    const currentRequest = ++requestNumber.current;
    const controller = new AbortController();
    requests.current.add(controller);
    let timeout;
    const token = loggedOut.current ? null : await Promise.race([
      getSeekyToken(seekyTokenUrl, controller.signal),
      // Answer before Seeky's five-second sign-in fallback, even if the API stalls.
      new Promise(resolve => {
        timeout = setTimeout(() => { controller.abort(); resolve(null); }, 4000);
      }),
    ]);
    clearTimeout(timeout);
    requests.current.delete(controller);
    // A logout, account switch, newer request, or unmount must never allow a
    // delayed response to authenticate the embed as the previous user.
    if (currentGeneration !== generation.current || currentRequest !== requestNumber.current ||
      iframeRef.current?.contentWindow !== frameWindow) return;
    frameWindow.postMessage({ type: 'SEEKY_TOKEN', token }, chatbotOrigin);
  }, [enabled, iframeRef]);

  useLayoutEffect(() => {
    const handleMessage = event => {
      if (!enabled || !isChatbotMessage(event, iframeRef.current)) return;
      if (event.data.type === 'CHATBOT_READY' || event.data.type === 'SEEKY_TOKEN_REQUEST') {
        frameLoaded.current = true;
        sendSeekyToken();
      }
    };
    const handleLogout = () => {
      invalidateRequests();
      loggedOut.current = true;
      if (chatbotOrigin) {
        iframeRef.current?.contentWindow?.postMessage({ type: 'USER_LOGOUT' }, chatbotOrigin);
      }
    };
    window.addEventListener('message', handleMessage);
    window.addEventListener('seekjobs:chatbot-logout', handleLogout);
    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('seekjobs:chatbot-logout', handleLogout);
      invalidateRequests();
    };
  }, [enabled, iframeRef, sendSeekyToken, invalidateRequests]);

  useLayoutEffect(() => {
    invalidateRequests();
    loggedOut.current = previousLogin.current && !isLoggedIn;
    if (loggedOut.current && chatbotOrigin) {
      iframeRef.current?.contentWindow?.postMessage({ type: 'USER_LOGOUT' }, chatbotOrigin);
    }
    previousLogin.current = isLoggedIn;
    if (frameLoaded.current) sendSeekyToken();
  }, [isLoggedIn, userId, userRole, iframeRef, sendSeekyToken, invalidateRequests]);

  return useCallback(() => {
    invalidateRequests();
    frameLoaded.current = true;
    sendSeekyToken();
  }, [invalidateRequests, sendSeekyToken]);
}
